import { useCallback, useEffect, useState } from 'react';

const API_BASE = process.env.REACT_APP_AUTH_BACKEND_URL || '';

const IN_PROGRESS = ['running', 'pending'];

/** Shallow Set equality so an unchanged poll doesn't trigger a re-render. */
function sameSet(a, b) {
  if (a.size !== b.size) return false;
  for (const v of a) {
    if (!b.has(v)) return false;
  }
  return true;
}

/**
 * Poll the user's workflow runs and expose the set of workflow IDs that
 * currently have an in-progress (running/pending) run on the server.
 *
 * The left-sidebar "run in progress" dot is driven from this — rather than
 * the session-local `activeRuns` map — so a workflow shows as running even
 * when its run was started in another tab/session or survived a reload.
 *
 * @param {object} [opts]
 * @param {number} [opts.pollMs=5000] - Poll interval in milliseconds.
 * @returns {{ runningWorkflowIds: Set<string>, refresh: () => void }}
 */
export function useUserRuns({ pollMs = 5000 } = {}) {
  const [runningWorkflowIds, setRunningWorkflowIds] = useState(
    () => new Set(),
  );

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/workflows/runs?limit=50`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = await res.json();
      const next = new Set(
        (data.runs || [])
          .filter((r) => IN_PROGRESS.includes(r.status))
          .map((r) => r.workflow_id)
          .filter(Boolean),
      );
      // Replace only on change so subscribers don't re-render every poll.
      setRunningWorkflowIds((prev) => (sameSet(prev, next) ? prev : next));
    } catch {
      /* non-fatal: keep the last known set */
    }
  }, []);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, pollMs);
    return () => clearInterval(id);
  }, [refresh, pollMs]);

  return { runningWorkflowIds, refresh };
}
